import React, { FC, useCallback, useMemo, useState } from "react";

import { Button, Popover, Slot, Text } from "@costar-ui/components";
import type { RequestTrainingModalDialogProps, ShareFeedbackModalDialogProps } from "@costar-ui/footer";
import { RequestTrainingModalDialog, ShareFeedbackModalDialog } from "@costar-ui/footer";
import { useSupportModalHandlers } from "@costar-ui/share-compositions";

import { HelpPopoverProps } from "./types";
import { getContactCardInfo } from "./utils";

import { useAuthNavConfigContext } from "$client/auth/nav-config";
import { AuthenticatedClientConfig, MainProductIds, useConfigContext } from "$common";

const HelpPopover: FC<HelpPopoverProps> = props => {
    const { resources, popoverOpen, setPopoverOpen, buttonRef, hideRequestTraining, productMode = "" } = props;
    const { userInfo } = useAuthNavConfigContext();
    const config = useConfigContext() as AuthenticatedClientConfig;
    const { options } = config || {};
    const apiBasePath = options?.hosts?.mfRootPath?.uri || "";
    const isStr = productMode === MainProductIds.STR;

    const [openTrainingModal, setOpenTrainingModal] = useState(false);
    const [openShareFeedbackModal, setOpenShareFeedbackModal] = useState(false);

    const onCloseTrainingModal = useCallback(() => {
        setOpenTrainingModal(false);
    }, [setOpenTrainingModal]);

    const onCloseShareFeedbackModal = useCallback(() => {
        setOpenShareFeedbackModal(false);
    }, [setOpenShareFeedbackModal]);

    const { contactId, locationId, firstName, lastName } = userInfo || {};

    const { contactCardData, onRequestContactCardData, onRequestTraining, onSubmitFeedback } = useSupportModalHandlers({
        apiBasePath,
        contactId: Number(contactId || 0),
        locationId: Number(locationId || 0),
        productMode,
        userAgentString: typeof window !== "undefined" ? window?.navigator?.userAgent : undefined,
        firstName,
        lastName,
    });

    const onHelpClick = useCallback(() => {
        const helpURI =
            userInfo?.locationCountryCode !== "GBR"
                ? options?.hosts?.newSearchHelp?.uri
                : options?.hosts?.newSearchHelpUk?.uri;
        setPopoverOpen(false);
        const helpURL = `${helpURI}/${productMode === "PUBLIC_RECORD" ? "publicrecordsearch" : "help"}`;
        window?.open(helpURL, "_blank")?.focus();
    }, [productMode, options, setPopoverOpen, userInfo]);

    const onRequestTrainingClicked = useCallback(() => {
        setPopoverOpen(false);
        setOpenTrainingModal(true);
        onRequestContactCardData();
    }, [onRequestContactCardData, setPopoverOpen, setOpenTrainingModal]);

    const onShareFeedbackClicked = useCallback(() => {
        setPopoverOpen(false);
        setOpenShareFeedbackModal(true);
    }, [setPopoverOpen, setOpenShareFeedbackModal]);

    const handlePopoverCloseOnInteractOutside = useCallback(
        (elem: Element) => {
            elem && setPopoverOpen(false);
            return true;
        },
        [setPopoverOpen]
    );

    const trainingModalProps: RequestTrainingModalDialogProps = useMemo(() => {
        const contactCard = getContactCardInfo(resources, contactCardData?.data, contactCardData?.isFetching);
        return {
            dialog: {
                heading: resources.requestTraining.heading(),
                actionLabel: resources.requestTraining.request(),
                cancelLabel: resources.requestTraining.cancel(),
                isOpen: openTrainingModal,
                onAction: onRequestTraining,
                onCancel: onCloseTrainingModal,
                onClose: onCloseTrainingModal,
            },
            infoText: resources.requestTraining.info(),
            contact: contactCard,
        };
    }, [resources, onCloseTrainingModal, onRequestTraining, openTrainingModal, contactCardData]);

    const shareFeedbackProps: ShareFeedbackModalDialogProps = useMemo(() => {
        return {
            dialog: {
                heading: resources.shareFeedback.title(),
                actionLabel: resources.shareFeedback.requestBtn(),
                cancelLabel: resources.shareFeedback.cancelBtn(),
                isOpen: openShareFeedbackModal,
                onAction: onSubmitFeedback,
                onCancel: onCloseShareFeedbackModal,
                onClose: onCloseShareFeedbackModal,
            },
            infoText: resources.shareFeedback.subtitle(),
            textareaPlaceholder: resources.shareFeedback.placeholder(),
        };
    }, [resources, onCloseShareFeedbackModal, onSubmitFeedback, openShareFeedbackModal]);

    return (
        <div>
            <Popover
                showArrow={true}
                isOpen={popoverOpen}
                onOpenChange={setPopoverOpen}
                triggerRef={buttonRef}
                shouldCloseOnInteractOutside={handlePopoverCloseOnInteractOutside}
            >
                <Slot direction="vertical" gap="md" padding="3xl" alignItems="start" justify="start">
                    {!isStr && (
                        <Button
                            variant="ghost-neutral"
                            noBorder
                            onPress={onHelpClick}
                            size="sm"
                            testId="open-help-button"
                        >
                            <Text>{resources.search.common.share.helpWithFeatures()}</Text>
                        </Button>
                    )}
                    {!isStr && !hideRequestTraining && (
                        <Button
                            variant="ghost-neutral"
                            noBorder
                            onPress={onRequestTrainingClicked}
                            size="sm"
                            testId="training-button"
                        >
                            <Text>{resources.search.common.share.requestTraining()}</Text>
                        </Button>
                    )}
                    <Button
                        variant="ghost-neutral"
                        noBorder
                        onPress={onShareFeedbackClicked}
                        size="sm"
                        testId="feedback-button"
                    >
                        <Text>{resources.search.common.share.shareFeedback()}</Text>
                    </Button>
                </Slot>
            </Popover>
            {openTrainingModal && <RequestTrainingModalDialog {...trainingModalProps} />}
            {openShareFeedbackModal && <ShareFeedbackModalDialog {...shareFeedbackProps} />}
        </div>
    );
};

export default HelpPopover;
